import type {
  CompetitionState,
  CompetitionType,
  CompetitionWindow,
  LifecycleAction,
  PaymentTiming,
} from '@concours/shared'

/** Concours tel que renvoyé par l'API d'administration. */
export interface Competition {
  id: string
  reference: string
  title: string
  session: string
  year: number
  type: CompetitionType
  typeLabel: string
  corps: string
  grade: string | null
  seats: number
  summary: string | null
  conditions: string | null
  requiredDocuments: string[]
  selectionRules: string | null
  eligibility?: { minAge: number | null; maxAge: number | null; ageReferenceDate: string | null }
  applicationPrefix: string
  paymentTiming: PaymentTiming
  feeAmount: number
  currency: string
  publicationAt: string | null
  opensAt: string | null
  closesAt: string | null
  paymentDeadlineAt: string | null
  status: CompetitionState
  window: CompetitionWindow
  statusChangedAt: string | null
  createdAt: string | null
  updatedAt: string | null
}

export interface Milestone {
  label: string
  startsAt: string
  endsAt: string | null
  isPublic: boolean
}

export interface Category {
  code: string
  label: string
  seats: number | null
}

export interface Notice {
  version: number
  fileName: string
  size: number
  sha256: string
  createdAt: string
}

export interface CompetitionDetail extends Competition {
  milestones: Milestone[]
  categories: Category[]
  notice: Notice | null
  form: { versionId: string; version: number; formId: string; code: string; title: string } | null
  activeDerogations: number
  lockedFields: string[]
  actions: LifecycleAction[]
  can: { update: boolean; extend: boolean; derogation: boolean; changeForm: boolean }
}

export interface HistoryEntry {
  id: number
  kind: 'transition' | 'extension' | 'derogation'
  from: CompetitionState | null
  to: CompetitionState | null
  actor: string | null
  system: boolean
  reason: string | null
  metadata: {
    closesAt?: { before: string | null; after: string | null }
    candidate?: string
    granted?: boolean
    automatic?: boolean
  } | null
  createdAt: string
}

export interface Derogation {
  id: string
  candidate: string
  reason: string
  validUntil: string
  grantedAt: string
  grantedBy: string | null
  revokedAt: string | null
  revokedBy: string | null
  revokeReason: string | null
  active: boolean
}

/** Champs éditables (formulaire de création / modification). */
export interface CompetitionForm {
  reference: string
  title: string
  session: string
  year: string
  type: CompetitionType
  corps: string
  grade: string
  seats: string
  summary: string
  conditions: string
  requiredDocuments: string[]
  selectionRules: string
  /** Conditions d'âge contrôlées automatiquement à la soumission (phase 9). */
  minAge: string
  maxAge: string
  ageReferenceDate: string
  applicationPrefix: string
  paymentTiming: PaymentTiming
  feeAmount: string
  publicationAt: string
  opensAt: string
  closesAt: string
  paymentDeadlineAt: string
}

/** ISO → valeur d'un <input type="datetime-local"> (heure locale). */
export function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** <input type="datetime-local"> → ISO (null si vide). */
export function fromLocalInput(value: string): string | null {
  return value ? new Date(value).toISOString() : null
}

export const emptyForm = (): CompetitionForm => ({
  reference: '',
  title: '',
  session: `Session ${new Date().getFullYear()}`,
  year: String(new Date().getFullYear()),
  type: 'direct',
  corps: '',
  grade: '',
  seats: '',
  summary: '',
  conditions: '',
  requiredDocuments: [],
  selectionRules: '',
  minAge: '',
  maxAge: '',
  ageReferenceDate: '',
  applicationPrefix: '',
  paymentTiming: 'before_submit',
  feeAmount: '',
  publicationAt: '',
  opensAt: '',
  closesAt: '',
  paymentDeadlineAt: '',
})

export function formFrom(c: Competition): CompetitionForm {
  return {
    reference: c.reference,
    title: c.title,
    session: c.session,
    year: String(c.year),
    type: c.type,
    corps: c.corps,
    grade: c.grade ?? '',
    seats: String(c.seats),
    summary: c.summary ?? '',
    conditions: c.conditions ?? '',
    requiredDocuments: c.requiredDocuments,
    selectionRules: c.selectionRules ?? '',
    minAge: c.eligibility?.minAge != null ? String(c.eligibility.minAge) : '',
    maxAge: c.eligibility?.maxAge != null ? String(c.eligibility.maxAge) : '',
    ageReferenceDate: c.eligibility?.ageReferenceDate ?? '',
    applicationPrefix: c.applicationPrefix,
    paymentTiming: c.paymentTiming,
    feeAmount: String(c.feeAmount),
    publicationAt: toLocalInput(c.publicationAt),
    opensAt: toLocalInput(c.opensAt),
    closesAt: toLocalInput(c.closesAt),
    paymentDeadlineAt: toLocalInput(c.paymentDeadlineAt),
  }
}

/** Formulaire → corps de requête (seulement les champs listés si `only`). */
export function payloadFrom(form: CompetitionForm, only?: (keyof CompetitionForm)[]) {
  const all: Record<string, unknown> = {
    reference: form.reference.trim(),
    title: form.title.trim(),
    session: form.session.trim(),
    year: Number(form.year),
    type: form.type,
    corps: form.corps.trim(),
    grade: form.grade.trim() || null,
    seats: Number(form.seats),
    summary: form.summary.trim() || null,
    conditions: form.conditions.trim() || null,
    requiredDocuments: form.requiredDocuments.map((d) => d.trim()).filter(Boolean),
    selectionRules: form.selectionRules.trim() || null,
    eligibility: {
      minAge: form.minAge ? Number(form.minAge) : null,
      maxAge: form.maxAge ? Number(form.maxAge) : null,
      ageReferenceDate: form.ageReferenceDate || null,
    },
    applicationPrefix: form.applicationPrefix.trim().toUpperCase(),
    paymentTiming: form.paymentTiming,
    feeAmount: form.paymentTiming === 'not_required' ? 0 : Number(form.feeAmount || 0),
    publicationAt: fromLocalInput(form.publicationAt),
    opensAt: fromLocalInput(form.opensAt),
    closesAt: fromLocalInput(form.closesAt),
    paymentDeadlineAt:
      form.paymentTiming === 'not_required' ? null : fromLocalInput(form.paymentDeadlineAt),
  }
  if (!only) return all
  // Les trois champs d'âge forment un seul objet `eligibility` côté API.
  const keys = new Set<string>(
    only.map((k) =>
      k === 'minAge' || k === 'maxAge' || k === 'ageReferenceDate' ? 'eligibility' : k
    )
  )
  return Object.fromEntries([...keys].map((k) => [k, all[k]]))
}

export const money = (amount: number, currency = 'XOF') =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency, maximumFractionDigits: 0 }).format(
    amount
  )

const dateTime = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })
export const when = (iso: string | null | undefined) => (iso ? dateTime.format(new Date(iso)) : '—')
